import { cssPxFromVisual, cssRectFromVisual, cssViewportSize, getPageZoom } from '@/lib/pageZoom'

/** Нижняя кромка фиксированной шапки модуля (CSS px до zoom). 0, если шапки нет. */
export function moduleHeaderBottom() {
  const header = document.querySelector('.navbar-dark')
  if (!header) return 0
  const bottom = cssPxFromVisual(header.getBoundingClientRect().bottom)
  return bottom > 0 ? bottom : 0
}

/**
 * Прячет часть fixed-оверлея, которая заехала на шапку.
 * Меню на body с z-index выше navbar рисуется поверх неё при прокрутке карточки.
 *
 * @param {HTMLElement | null | undefined} el
 */
export function clipBelowModuleHeader(el) {
  if (!el) return
  const limit = moduleHeaderBottom()
  if (!limit) {
    el.style.clipPath = ''
    return
  }
  const elTop = cssPxFromVisual(el.getBoundingClientRect().top)
  const overlap = limit - elTop
  el.style.clipPath = overlap > 0.5 ? `inset(${overlap}px 0 0 0)` : ''
}

/**
 * clip-path для меню, верх которого задан координатой viewport (visual → CSS px).
 *
 * @param {number} viewportTopVisual
 * @returns {string}
 */
export function clipPathBelowModuleHeader(viewportTopVisual) {
  const limit = moduleHeaderBottom()
  if (!limit) return ''
  const overlap = limit - cssPxFromVisual(viewportTopVisual)
  return overlap > 0.5 ? `inset(${overlap}px 0 0 0)` : ''
}

const MENU_MAX_HEIGHT = 220
const MENU_GAP = 6
const MENU_EDGE = 8

/**
 * Высота и сторона открытия fixed-меню по свободному месту во viewport.
 * На масштабе 150% окно в CSS-пикселях ниже, и список с фиксированными 220px обрезается.
 *
 * @param {DOMRect} anchorRectVisual Рамка триггера из getBoundingClientRect (visual).
 * @param {{ maxHeight?: number }} [options]
 * @returns {{ dropUp: boolean, maxHeight: number, left: number, width: number, top: number, bottom: number, cssRect: ReturnType<typeof cssRectFromVisual> }}
 */
export function viewportMenuPlacement(anchorRectVisual, options = {}) {
  const cssRect = cssRectFromVisual(anchorRectVisual)
  const maxCap = options.maxHeight ?? MENU_MAX_HEIGHT
  const header = moduleHeaderBottom()
  // innerHeight без /zoom расходится с cssRect при page zoom ≠ 100%.
  const { height: viewH } = cssViewportSize()
  const spaceBelow = viewH - cssRect.bottom - MENU_GAP - MENU_EDGE
  const spaceAbove = cssRect.top - header - MENU_GAP - MENU_EDGE
  const dropUp = spaceBelow < Math.min(maxCap, 160) && spaceAbove > spaceBelow
  const available = Math.max(0, dropUp ? spaceAbove : spaceBelow)
  const maxHeight = Math.min(maxCap, available)
  // Ширина = якорь (RA PIL / Medical Lead и др. в правой колонке). Не ужимаем под
  // spaceRight: при неверном viewW список становился уже поля; сдвиг left ломал выравнивание.
  let left = cssRect.left
  const width = cssRect.width
  if (left < MENU_EDGE) {
    left = MENU_EDGE
  }
  return {
    dropUp,
    maxHeight,
    left,
    width,
    top: cssRect.top,
    bottom: cssRect.bottom,
    cssRect,
  }
}

/**
 * Ставит меню в body и вписывает его в viewport: вниз или вверх, со скроллом.
 *
 * @param {DOMRect} anchorRectVisual Рамка триггера (getBoundingClientRect).
 * @param {HTMLElement | null | undefined} menuEl Список.
 * @param {{ zIndex?: string, maxHeight?: number }} [options]
 * @returns {boolean} true, если меню открыто вверх.
 */
export function applyViewportMenu(anchorRectVisual, menuEl, options = {}) {
  if (!menuEl) return false
  mountMenuOnBody(menuEl)
  const { dropUp, maxHeight, left, width, top, bottom } = viewportMenuPlacement(
    anchorRectVisual,
    options
  )
  menuEl.style.position = 'fixed'
  menuEl.style.left = `${left}px`
  menuEl.style.width = `${width}px`
  menuEl.style.minWidth = `${width}px`
  menuEl.style.maxWidth = `${width}px`
  menuEl.style.right = 'auto'
  menuEl.style.zIndex = options.zIndex || '2000'
  menuEl.style.boxSizing = 'border-box'
  menuEl.style.marginTop = '0'
  if (dropUp) {
    menuEl.style.top = 'auto'
    menuEl.style.bottom = `${cssViewportSize().height - top + MENU_GAP}px`
  } else {
    menuEl.style.bottom = 'auto'
    menuEl.style.top = `${bottom + MENU_GAP}px`
  }
  fitMenuScroll(menuEl, maxHeight)
  clipBelowModuleHeader(menuEl)
  return dropUp
}

/**
 * Choices скроллит внутренний .choices__list. Остальные меню скроллятся целиком.
 *
 * @param {HTMLElement} menuEl
 * @param {number} maxHeight
 */
function fitMenuScroll(menuEl, maxHeight) {
  const inner = menuEl.querySelector('.choices__list')
  if (!(inner instanceof HTMLElement)) {
    menuEl.style.maxHeight = `${maxHeight}px`
    menuEl.style.overflowX = 'hidden'
    menuEl.style.overflowY = 'auto'
    return
  }
  const search = menuEl.querySelector('input.choices__input')
  const searchH =
    search instanceof HTMLElement
      ? Math.ceil(cssPxFromVisual(search.getBoundingClientRect().height))
      : 0
  const chrome = searchH > 0 ? searchH + 8 : 8
  menuEl.style.maxHeight = 'none'
  menuEl.style.overflow = 'visible'
  inner.style.maxHeight = `${Math.max(0, maxHeight - chrome)}px`
  inner.style.overflowX = 'hidden'
  inner.style.overflowY = 'auto'
}

/**
 * Choices выбирает пункт по mousedown внутри .choices и закрывает список,
 * если click пришёл снаружи. После переноса меню на body оба случая ломаются.
 *
 * @param {{ dropdown?: { element?: HTMLElement }, _onMouseDown?: Function, _onClick?: Function }} inst
 */
export function bindChoicesMenuEvents(inst) {
  const dropdown = inst?.dropdown?.element
  if (!(dropdown instanceof HTMLElement) || dropdown.dataset.ltmsChoicesBound === '1') return
  if (typeof inst._onMouseDown !== 'function' || typeof inst._onClick !== 'function') return
  dropdown.dataset.ltmsChoicesBound = '1'
  dropdown.addEventListener('mousedown', inst._onMouseDown, true)
  const origOnClick = inst._onClick
  inst._onClick = function onChoicesClick(event) {
    if (event?.target instanceof Node && dropdown.contains(event.target)) return
    return origOnClick.call(inst, event)
  }
}

/**
 * Сбрасывает inline-позицию меню и возвращает его в исходного родителя.
 *
 * @param {HTMLElement | null | undefined} menuEl
 */
export function resetViewportMenu(menuEl) {
  if (!menuEl) return
  for (const prop of [
    'position',
    'left',
    'top',
    'bottom',
    'width',
    'minWidth',
    'maxWidth',
    'right',
    'zIndex',
    'maxHeight',
    'overflow',
    'overflowX',
    'overflowY',
    'boxSizing',
    'marginTop',
    'clipPath',
  ]) {
    menuEl.style[prop] = ''
  }
  const inner = menuEl.querySelector('.choices__list')
  if (inner instanceof HTMLElement) {
    inner.style.maxHeight = ''
    inner.style.overflow = ''
    inner.style.overflowX = ''
    inner.style.overflowY = ''
  }
  restoreMenuHome(menuEl)
}

/**
 * Выносит меню на body, чтобы fieldset и overflow карточки его не обрезали.
 *
 * @param {HTMLElement} menuEl
 */
function mountMenuOnBody(menuEl) {
  if (menuEl.parentElement === document.body) return
  menuEl._ltmsMenuHome = menuEl.parentElement
  document.body.appendChild(menuEl)
}

/**
 * @param {HTMLElement} menuEl
 */
function restoreMenuHome(menuEl) {
  const home = menuEl._ltmsMenuHome
  if (home instanceof HTMLElement && home.isConnected && menuEl.parentElement !== home) {
    home.appendChild(menuEl)
  }
  delete menuEl._ltmsMenuHome
}

// re-export for callers that need zoom helpers next to placement
export { getPageZoom, cssPxFromVisual, cssRectFromVisual, cssViewportSize }
